import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { setupTotp, confirmTotp, type TotpSetup } from '../../auth/mfaApi.js';
import { ApiError } from '../../api/client.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

type Phase = 'loading' | 'ready' | 'success';

const CODE_PATTERN = /^\d{6}$/;

/**
 * MFA TOTP enrolment (M02 §16 / M02-G01).
 *
 * On mount the page asks the backend to begin enrolment; the server issues a
 * fresh per-user secret and otpauth:// provisioning URI. The secret is NEVER
 * generated or hardcoded on the client. The user scans the QR (or types the
 * setup key manually) into an authenticator app, then confirms the first
 * 6-digit code to activate TOTP as their primary factor.
 */
export function MfaEnrolPage(): ReactNode {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('loading');
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [code, setCode] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const res = await setupTotp();
        if (active) {
          setSetup(res);
          setPhase('ready');
        }
      } catch {
        if (active) {
          setSetupError(t('auth.mfa.enrol.setupError'));
          setPhase('ready');
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setConfirmError(null);
    if (!CODE_PATTERN.test(code)) {
      setConfirmError(t('auth.mfa.enrol.invalidCode'));
      return;
    }
    setSubmitting(true);
    try {
      await confirmTotp(code);
      setPhase('success');
    } catch (err) {
      const msg =
        err instanceof ApiError &&
        typeof err.body === 'object' &&
        err.body !== null &&
        'message' in err.body
          ? String((err.body as { message: unknown }).message)
          : t('auth.mfa.enrol.confirmError');
      setConfirmError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === 'success') {
    return (
      <main aria-label="mfa-enrol-success">
        <h1>{t('auth.mfa.enrol.successTitle')}</h1>
        <p>
          {t('auth.mfa.enrol.successBody')}
        </p>
      </main>
    );
  }

  return (
    <main aria-label="mfa-enrol">
      <h1>{t('auth.mfa.enrol.title')}</h1>

      {phase === 'loading' ? (
        <p aria-label="mfa-loading">{t('auth.mfa.enrol.loading')}</p>
      ) : setupError ? (
        <p role="alert">{setupError}</p>
      ) : setup ? (
        <>
          <p>
            {t('auth.mfa.enrol.scanInstruction')}
          </p>

          <div aria-label="mfa-qr">
            <QRCodeSVG value={setup.otpauthUri} size={192} role="img" aria-label="mfa-qr-code" />
          </div>

          <p>
            {t('auth.mfa.enrol.issuerLabel')} <strong aria-label="mfa-issuer">{setup.issuer}</strong>
          </p>

          <button type="button" onClick={() => setRevealed((v) => !v)}>
            {revealed ? t('auth.mfa.enrol.hideKey') : t('auth.mfa.enrol.showKey')}
          </button>
          {revealed ? <code aria-label="mfa-secret">{setup.secret}</code> : null}

          <form onSubmit={onSubmit} aria-label="mfa-confirm-form">
            <label htmlFor="mfa-code">{t('auth.mfa.enrol.codeLabel')}</label>
            <input
              id="mfa-code"
              name="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            {confirmError ? <p role="alert">{confirmError}</p> : null}
            <button type="submit" disabled={submitting}>
              {submitting ? t('auth.login.verifying') : t('auth.mfa.enrol.verifyEnable')}
            </button>
          </form>
        </>
      ) : null}
    </main>
  );
}
